<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { onMounted, ref, watch } from "vue";
import { PurchasedProduct, Trainer } from "../../types/PersologOnlineTypes.ts";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../../api";
import { useToast } from "primevue/usetoast";

type ReclaimableProduct = {
    productNo: string;
    productName: string;
    quantity: number;
};

const { t, locale } = useI18n();

const props = defineProps({
    visibleToggle: {
        type: Boolean,
        default: false,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    account: {
        type: Object as () => Trainer,
        required: true,
    },
    // Prefill for the per-row entry point in the purchases table: pre-selects the product and
    // proposes the row's quantity (still clamped to the sub-account's live stock).
    presetProduct: {
        type: String,
        default: "",
    },
    presetQuantity: {
        type: Number,
        default: 0,
    },
});

const toast = useToast();
const loading = ref(false);
const visible = ref(props.visibleToggle);
const accountProducts = ref<ReclaimableProduct[]>([]);
const selectedProduct = ref<ReclaimableProduct>();
const selectedQuantity = ref<number>(0);

const emit = defineEmits(["update:visible", "retrieved"]);

watch(
    () => visible.value,
    () => {
        emit("update:visible", visible.value);
    },
);

watch(
    () => props.visibleToggle,
    () => {
        visible.value = props.visibleToggle;
    },
);

/**
 * Takes the selected amount of profiles back from the sub-account. The backend clamps to the
 * sub-account's stock, so a smaller retrieved amount is reported as a partial success.
 */
async function reclaimProducts() {
    if (props.trainer && props.account && selectedProduct.value && selectedQuantity.value > 0) {
        loading.value = true;

        const product: PurchasedProduct = {
            id: 0,
            product: selectedProduct.value.productNo,
            quantity: selectedQuantity.value,
            trainer: props.account.id,
        };

        try {
            const result = await ProfileAdministrationAPI.retrieveProducts(
                props.trainer.id,
                props.account.id,
                product,
            );
            toastReclaimSuccess(
                selectedProduct.value.productName,
                result.retrievedQuantity,
                result.requestedQuantity,
                `${props.account.firstname} ${props.account.lastname}`,
            );
            emit("retrieved", result);
            visible.value = false;
        } catch (error) {
            console.error("Error reclaiming products:", error);
            toastReclaimError(`${props.account.firstname} ${props.account.lastname}`);
        } finally {
            loading.value = false;
        }
    }
}

function toastReclaimSuccess(product: string, retrieved: number, requested: number, account: string) {
    toast.add({
        severity: retrieved < requested ? "warn" : "success",
        summary: t("header.accountSettings.accountLinks.reclaim.headingSuccess"),
        detail:
            retrieved < requested
                ? t("header.accountSettings.accountLinks.reclaim.messagePartial", {
                      product: product,
                      retrieved: retrieved,
                      requested: requested,
                      account: account,
                  })
                : t("header.accountSettings.accountLinks.reclaim.message", {
                      product: product,
                      quantity: retrieved,
                      account: account,
                  }),
        life: 10000,
    });
}

function toastReclaimError(account: string) {
    toast.add({
        severity: "error",
        summary: t("header.accountSettings.accountLinks.reclaim.errorHeading"),
        detail: t("header.accountSettings.accountLinks.reclaim.errorMessage", { account: account }),
        life: 10000,
    });
}

onMounted(async () => {
    // The product list and the shown maximum come from the sub-account's live stock,
    // not from the distributor's own products.
    const [texts, stock] = await Promise.all([
        LanguageManagementAPI.fetchTexts("products"),
        ProfileAdministrationAPI.fetchPurchasedProducts(props.account.id),
    ]);
    const names = Object.fromEntries(
        texts.map((id) => [
            id.identifier,
            id.texts.find((text) => text.language === locale.value)?.text ??
                id.texts.find((text) => text.language === "en")?.text ??
                id.identifier,
        ]),
    );
    accountProducts.value = stock
        .filter((purchase) => purchase.quantity > 0)
        .map((purchase) => ({
            productNo: purchase.product,
            productName: names[purchase.product]
                ? `${names[purchase.product]} (${purchase.product.toUpperCase()})`
                : purchase.product.toUpperCase(),
            quantity: purchase.quantity,
        }));

    if (props.presetProduct) {
        selectedProduct.value = accountProducts.value.find(
            (product) => product.productNo.toLowerCase() === props.presetProduct.toLowerCase(),
        );
        if (selectedProduct.value && props.presetQuantity > 0) {
            selectedQuantity.value = Math.min(props.presetQuantity, selectedProduct.value.quantity);
        }
    }
});
</script>
<template>
    <Toast />
    <Dialog
        v-model:visible="visible"
        modal
        :header="t('header.accountSettings.accountLinks.reclaim.header')"
        position="center"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <p class="reclaim-hint">
            {{
                t("header.accountSettings.accountLinks.reclaim.hint", {
                    account: `${props.account.firstname} ${props.account.lastname}`,
                })
            }}
        </p>
        <div class="form-container">
            <div class="dropdown-container">
                <Select
                    :id="`reclaim-dialog_dropdown-product-${account.id}`"
                    v-model="selectedProduct"
                    :options="accountProducts"
                    filter
                    option-label="productName"
                    :placeholder="t('fields.search')"
                    class="w-full md:w-56"
                />
                <!-- Renders only once a product is selected, which is also when the
                     quantity input's max stops being 0 — tests wait on it before typing. -->
                <label
                    v-if="selectedProduct"
                    :id="`reclaim-dialog_available-${account.id}`"
                    :for="`reclaim-dialog_input-quantity-${account.id}`"
                    class="quantity-label"
                >
                    {{ t("common.profiles.availableProfiles") }}: <strong>{{ selectedProduct?.quantity ?? 0 }}</strong>
                </label>
            </div>
            <div class="input-number-container">
                <InputNumber
                    v-model="selectedQuantity"
                    :input-id="`reclaim-dialog_input-quantity-${account.id}`"
                    show-buttons
                    button-layout="horizontal"
                    decrement-button-class="p-button-secondary"
                    increment-button-class="p-button-secondary"
                    increment-button-icon="pi pi-plus"
                    decrement-button-icon="pi pi-minus"
                    mode="decimal"
                    :min="0"
                    :max="selectedProduct?.quantity ?? 0"
                />
            </div>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2 mt-8">
                <Button
                    severity="secondary"
                    :label="t('buttons.cancel')"
                    icon="pi pi-times"
                    @click="visible = false"
                    size="small"
                    text
                />
                <Button
                    severity="secondary"
                    :id="`reclaim-dialog_btn-submit-${account.id}`"
                    @click="reclaimProducts"
                    :label="t('buttons.reclaim')"
                    :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-history'"
                    size="small"
                    autofocus
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.reclaim-hint {
    max-width: 32rem;
    margin: 0.5rem 0 0;
}

/* Align items at the start of the container */
.form-container {
    margin-top: 1rem;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
}

.dropdown-container {
    display: flex;
    flex-direction: column;
}

.quantity-label {
    font-size: 0.875rem;
    margin-top: 0.5rem;
}

/* Align InputNumber vertically with Select */
.input-number-container {
    display: flex;
    align-items: center;
    height: auto;
}
</style>
